import { supabase } from '@/integrations/supabase/client';
import { ModuloFotovoltaico, CreateModuloFotovoltaico, UpdateModuloFotovoltaico } from '@/types/modulos';

// Tipo para dados do banco
interface ModuloDB {
  id: string;
  marca: string;
  modelo: string;
  potencia: number;
  tecnologia: string;
  dimensoes_comprimento: number;
  dimensoes_largura: number;
  dimensoes_espessura: number;
  peso: number;
  tipo_celula: string;
  potencia_maxima: number;
  tensao_circuito_aberto: number;
  tensao_potencia_maxima: number;
  corrente_curto_circuito: number;
  corrente_potencia_maxima: number;
  eficiencia_modulo: number;
  garantia_fabricacao: number;
  garantia_produtividade: number;
  preco: number;
  imagem_url?: string | null;
  created_at: string;
  updated_at: string;
}

// Tipo para dados de atualização no banco
type UpdateModuloData = {
  marca?: string;
  modelo?: string;
  potencia?: number;
  tecnologia?: string;
  dimensoes_comprimento?: number;
  dimensoes_largura?: number;
  dimensoes_espessura?: number;
  peso?: number;
  tipo_celula?: string;
  potencia_maxima?: number;
  tensao_circuito_aberto?: number;
  tensao_potencia_maxima?: number;
  corrente_curto_circuito?: number;
  corrente_potencia_maxima?: number;
  eficiencia_modulo?: number;
  garantia_fabricacao?: number;
  garantia_produtividade?: number;
  imagem_url?: string | null;
};

export const modulosService = {
  async uploadImagem(moduloId: string, file: File): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${moduloId}_${Date.now()}.${fileExt}`;
    const filePath = `modulos_fotovoltaicos/${fileName}`;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Tipo de arquivo não permitido. Use JPEG, PNG, GIF ou WebP.');
    }

    const { error } = await supabase.storage
      .from('modulos_fotovoltaicos')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type,
      });

    if (error) {
      console.error('Erro no upload da imagem:', error);
      throw new Error(`Erro ao fazer upload da imagem: ${error.message}`);
    }

    const { data: urlData } = supabase.storage.from('modulos_fotovoltaicos').getPublicUrl(filePath);
    return urlData.publicUrl;
  },

  // Buscar todos os módulos
  async getAll(): Promise<ModuloFotovoltaico[]> {
    // Debug: Verificar estado da sessão
    const { data: { session } } = await supabase.auth.getSession();
    if (process.env.NODE_ENV === 'development') {
      console.log('🔐 modulosService.getAll - Estado da sessão:', {
        hasSession: !!session,
        userId: session?.user?.id,
        accessToken: session?.access_token ? 'Presente' : 'Ausente',
        expiresAt: session?.expires_at,
        currentTime: Math.floor(Date.now() / 1000)
      });
    }

    const { data, error } = await supabase
      .from('modulos_fotovoltaicos')
      .select('*')
      .order('marca', { ascending: true });

    if (error) {
      console.error('Erro ao buscar módulos:', error);
      throw new Error('Erro ao buscar módulos fotovoltaicos');
    }

    if (!data) {
      return [];
    }

    // Transformar dados do banco para o formato da interface
    return (data as ModuloDB[]).map((item: ModuloDB) => ({
      id: item.id,
      marca: item.marca || '',
      modelo: item.modelo || '',
      potencia: item.potencia || 0,
      tecnologia: item.tecnologia || '',
      dimensoes: {
        comprimento: item.dimensoes_comprimento || 0,
        largura: item.dimensoes_largura || 0,
        espessura: item.dimensoes_espessura || 0
      },
      peso: item.peso || 0,
      tipoCelula: item.tipo_celula || '',
      potenciaMaxima: item.potencia_maxima || 0,
      tensaoCircuitoAberto: item.tensao_circuito_aberto || 0,
      tensaoPotenciaMaxima: item.tensao_potencia_maxima || 0,
      correnteCurtoCircuito: item.corrente_curto_circuito || 0,
      correntePotenciaMaxima: item.corrente_potencia_maxima || 0,
      eficienciaModulo: item.eficiencia_modulo || 0,
      garantiaFabricacao: item.garantia_fabricacao || 0,
      garantiaProdutividade: item.garantia_produtividade || 0,
      imagemUrl: item.imagem_url ?? null,
      createdAt: item.created_at || '',
      updatedAt: item.updated_at || ''
    }));
  },

  // Buscar módulo por ID
  async getById(id: string): Promise<ModuloFotovoltaico | null> {
    const { data, error } = await supabase
      .from('modulos_fotovoltaicos')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Erro ao buscar módulo:', error);
      return null;
    }

    if (!data) return null;

    const moduloData = data as ModuloDB;

    // Transformar dados do banco para o formato da interface
    return {
      id: moduloData.id,
      marca: moduloData.marca || '',
      modelo: moduloData.modelo || '',
      potencia: moduloData.potencia || 0,
      tecnologia: moduloData.tecnologia || '',
      dimensoes: {
        comprimento: moduloData.dimensoes_comprimento || 0,
        largura: moduloData.dimensoes_largura || 0,
        espessura: moduloData.dimensoes_espessura || 0
      },
      peso: moduloData.peso || 0,
      tipoCelula: moduloData.tipo_celula || '',
      potenciaMaxima: moduloData.potencia_maxima || 0,
      tensaoCircuitoAberto: moduloData.tensao_circuito_aberto || 0,
      tensaoPotenciaMaxima: moduloData.tensao_potencia_maxima || 0,
      correnteCurtoCircuito: moduloData.corrente_curto_circuito || 0,
      correntePotenciaMaxima: moduloData.corrente_potencia_maxima || 0,
      eficienciaModulo: moduloData.eficiencia_modulo || 0,
      garantiaFabricacao: moduloData.garantia_fabricacao || 0,
      garantiaProdutividade: moduloData.garantia_produtividade || 0,
      imagemUrl: moduloData.imagem_url ?? null,
      createdAt: moduloData.created_at || '',
      updatedAt: moduloData.updated_at || ''
    };
  },

  // Criar novo módulo
  async create(modulo: CreateModuloFotovoltaico): Promise<ModuloFotovoltaico> {
    // Debug: Verificar estado da sessão antes de criar
    const { data: { session } } = await supabase.auth.getSession();
    if (process.env.NODE_ENV === 'development') {
      console.log('🔐 modulosService.create - Estado da sessão:', {
        hasSession: !!session,
        userId: session?.user?.id,
        accessToken: session?.access_token ? 'Presente' : 'Ausente',
        expiresAt: session?.expires_at,
        currentTime: Math.floor(Date.now() / 1000),
        isExpired: session?.expires_at ? Math.floor(Date.now() / 1000) > session.expires_at : false
      });
    }

    // Verificar se a sessão está ativa
    if (!session) {
      throw new Error('Sessão não encontrada. Faça login novamente.');
    }

    // Verificar se o token não expirou
    if (session.expires_at && Math.floor(Date.now() / 1000) > session.expires_at) {
      // Tentar fazer refresh do token
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 Token expirado, tentando refresh...');
      }
      
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !refreshData.session) {
        throw new Error('Sessão expirada e não foi possível renovar. Faça login novamente.');
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ Token renovado com sucesso');
      }
    }

    const insertData = {
      marca: modulo.marca,
      modelo: modulo.modelo,
      potencia: modulo.potencia,
      tecnologia: modulo.tecnologia,
      dimensoes_comprimento: modulo.dimensoes.comprimento,
      dimensoes_largura: modulo.dimensoes.largura,
      dimensoes_espessura: modulo.dimensoes.espessura,
      peso: modulo.peso,
      tipo_celula: modulo.tipoCelula,
      potencia_maxima: modulo.potenciaMaxima,
      tensao_circuito_aberto: modulo.tensaoCircuitoAberto,
      tensao_potencia_maxima: modulo.tensaoPotenciaMaxima,
      corrente_curto_circuito: modulo.correnteCurtoCircuito,
      corrente_potencia_maxima: modulo.correntePotenciaMaxima,
      eficiencia_modulo: modulo.eficienciaModulo,
      garantia_fabricacao: modulo.garantiaFabricacao,
      garantia_produtividade: modulo.garantiaProdutividade,
      imagem_url: modulo.imagemUrl ?? null,
    };

    const { data, error } = await supabase
      .from('modulos_fotovoltaicos')
      .insert(insertData)
      .select()
      .single();

    if (error) {
      console.error('Erro ao criar módulo:', error);
      // Log mais detalhado do erro
      if (process.env.NODE_ENV === 'development') {
        console.error('🔍 Detalhes do erro:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
      }
      throw new Error('Erro ao criar módulo fotovoltaico');
    }

    if (!data) {
      throw new Error('Módulo criado mas dados não retornados');
    }

    const moduloData = data as ModuloDB;

    // Transformar dados do banco para o formato da interface
    return {
      id: moduloData.id,
      marca: moduloData.marca || '',
      modelo: moduloData.modelo || '',
      potencia: moduloData.potencia || 0,
      tecnologia: moduloData.tecnologia || '',
      dimensoes: {
        comprimento: moduloData.dimensoes_comprimento || 0,
        largura: moduloData.dimensoes_largura || 0,
        espessura: moduloData.dimensoes_espessura || 0
      },
      peso: moduloData.peso || 0,
      tipoCelula: moduloData.tipo_celula || '',
      potenciaMaxima: moduloData.potencia_maxima || 0,
      tensaoCircuitoAberto: moduloData.tensao_circuito_aberto || 0,
      tensaoPotenciaMaxima: moduloData.tensao_potencia_maxima || 0,
      correnteCurtoCircuito: moduloData.corrente_curto_circuito || 0,
      correntePotenciaMaxima: moduloData.corrente_potencia_maxima || 0,
      eficienciaModulo: moduloData.eficiencia_modulo || 0,
      garantiaFabricacao: moduloData.garantia_fabricacao || 0,
      garantiaProdutividade: moduloData.garantia_produtividade || 0,
      imagemUrl: moduloData.imagem_url ?? null,
      createdAt: moduloData.created_at || '',
      updatedAt: moduloData.updated_at || ''
    };
  },

  // Atualizar módulo existente
  async update(id: string, modulo: UpdateModuloFotovoltaico): Promise<ModuloFotovoltaico> {
    const updateData: UpdateModuloData = {};
    
    if (modulo.marca !== undefined) updateData.marca = modulo.marca;
    if (modulo.modelo !== undefined) updateData.modelo = modulo.modelo;
    if (modulo.potencia !== undefined) updateData.potencia = modulo.potencia;
    if (modulo.tecnologia !== undefined) updateData.tecnologia = modulo.tecnologia;
    if (modulo.dimensoes?.comprimento !== undefined) updateData.dimensoes_comprimento = modulo.dimensoes.comprimento;
    if (modulo.dimensoes?.largura !== undefined) updateData.dimensoes_largura = modulo.dimensoes.largura;
    if (modulo.dimensoes?.espessura !== undefined) updateData.dimensoes_espessura = modulo.dimensoes.espessura;
    if (modulo.peso !== undefined) updateData.peso = modulo.peso;
    if (modulo.tipoCelula !== undefined) updateData.tipo_celula = modulo.tipoCelula;
    if (modulo.potenciaMaxima !== undefined) updateData.potencia_maxima = modulo.potenciaMaxima;
    if (modulo.tensaoCircuitoAberto !== undefined) updateData.tensao_circuito_aberto = modulo.tensaoCircuitoAberto;
    if (modulo.tensaoPotenciaMaxima !== undefined) updateData.tensao_potencia_maxima = modulo.tensaoPotenciaMaxima;
    if (modulo.correnteCurtoCircuito !== undefined) updateData.corrente_curto_circuito = modulo.correnteCurtoCircuito;
    if (modulo.correntePotenciaMaxima !== undefined) updateData.corrente_potencia_maxima = modulo.correntePotenciaMaxima;
    if (modulo.eficienciaModulo !== undefined) updateData.eficiencia_modulo = modulo.eficienciaModulo;
    if (modulo.garantiaFabricacao !== undefined) updateData.garantia_fabricacao = modulo.garantiaFabricacao;
    if (modulo.garantiaProdutividade !== undefined) updateData.garantia_produtividade = modulo.garantiaProdutividade;
    if (modulo.imagemUrl !== undefined) updateData.imagem_url = modulo.imagemUrl;

    const { data, error } = await supabase
      .from('modulos_fotovoltaicos')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Erro ao atualizar módulo:', error);
      throw new Error('Erro ao atualizar módulo fotovoltaico');
    }

    if (!data) {
      throw new Error('Módulo não encontrado para atualização');
    }

    const moduloData = data as ModuloDB;

    // Transformar dados do banco para o formato da interface
    return {
      id: moduloData.id,
      marca: moduloData.marca || '',
      modelo: moduloData.modelo || '',
      potencia: moduloData.potencia || 0,
      tecnologia: moduloData.tecnologia || '',
      dimensoes: {
        comprimento: moduloData.dimensoes_comprimento || 0,
        largura: moduloData.dimensoes_largura || 0,
        espessura: moduloData.dimensoes_espessura || 0
      },
      peso: moduloData.peso || 0,
      tipoCelula: moduloData.tipo_celula || '',
      potenciaMaxima: moduloData.potencia_maxima || 0,
      tensaoCircuitoAberto: moduloData.tensao_circuito_aberto || 0,
      tensaoPotenciaMaxima: moduloData.tensao_potencia_maxima || 0,
      correnteCurtoCircuito: moduloData.corrente_curto_circuito || 0,
      correntePotenciaMaxima: moduloData.corrente_potencia_maxima || 0,
      eficienciaModulo: moduloData.eficiencia_modulo || 0,
      garantiaFabricacao: moduloData.garantia_fabricacao || 0,
      garantiaProdutividade: moduloData.garantia_produtividade || 0,
      imagemUrl: moduloData.imagem_url ?? null,
      createdAt: moduloData.created_at || '',
      updatedAt: moduloData.updated_at || ''
    };
  },

  // Excluir módulo
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('modulos_fotovoltaicos')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Erro ao excluir módulo:', error);
      throw new Error('Erro ao excluir módulo fotovoltaico');
    }
  },

  // Buscar módulos por filtros
  async search(query: string): Promise<ModuloFotovoltaico[]> {
    const { data, error } = await supabase
      .from('modulos_fotovoltaicos')
      .select('*')
      .or(`marca.ilike.%${query}%,modelo.ilike.%${query}%,tecnologia.ilike.%${query}%`)
      .order('marca', { ascending: true });

    if (error) {
      console.error('Erro ao buscar módulos:', error);
      throw new Error('Erro ao buscar módulos fotovoltaicos');
    }

    if (!data) {
      return [];
    }

    // Transformar dados do banco para o formato da interface
    return (data as ModuloDB[]).map((item: ModuloDB) => ({
      id: item.id,
      marca: item.marca || '',
      modelo: item.modelo || '',
      potencia: item.potencia || 0,
      tecnologia: item.tecnologia || '',
      dimensoes: {
        comprimento: item.dimensoes_comprimento || 0,
        largura: item.dimensoes_largura || 0,
        espessura: item.dimensoes_espessura || 0
      },
      peso: item.peso || 0,
      tipoCelula: item.tipo_celula || '',
      potenciaMaxima: item.potencia_maxima || 0,
      tensaoCircuitoAberto: item.tensao_circuito_aberto || 0,
      tensaoPotenciaMaxima: item.tensao_potencia_maxima || 0,
      correnteCurtoCircuito: item.corrente_curto_circuito || 0,
      correntePotenciaMaxima: item.corrente_potencia_maxima || 0,
      eficienciaModulo: item.eficiencia_modulo || 0,
      garantiaFabricacao: item.garantia_fabricacao || 0,
      garantiaProdutividade: item.garantia_produtividade || 0,
      imagemUrl: item.imagem_url ?? null,
      createdAt: item.created_at || '',
      updatedAt: item.updated_at || ''
    }));
  }
};
